import React, { useEffect } from "react";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { fetchGet } from "../../../fetchers";
import angkaTerbilang from "@develoka/angka-terbilang-js";
import LOGO_RKS from "../../../assets/logo/LOGO_RKS.png";
import { useUserBisnisUnit } from "../../../hooks/useUserBisnisUnit";

const SO_UNIT = "U0002";

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 50,
    paddingHorizontal: 40,
    fontFamily: "Helvetica",
  },
  // Header: logo kanan, judul tengah
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  logo: {
    width: 120,
    height: "auto",
  },
  titleBlock: {
    flex: 1,
    alignItems: "center",
    paddingTop: 4,
  },
  titleMain: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
  },
  titleSub: {
    fontSize: 10,
    marginTop: 2,
    color: "#444",
  },
  divider: {
    borderBottomWidth: 1.5,
    borderBottomColor: "#000",
    marginBottom: 10,
  },
  // Info 2-kolom
  infoSection: {
    flexDirection: "row",
    marginBottom: 3,
  },
  infoLeft: {
    width: "50%",
    flexDirection: "row",
  },
  infoRight: {
    width: "50%",
    flexDirection: "row",
  },
  infoLabel: {
    width: 80,
    fontSize: 9,
    color: "#333",
  },
  infoColon: {
    width: 10,
    fontSize: 9,
  },
  infoValue: {
    flex: 1,
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
  },
  infoValueNormal: {
    flex: 1,
    fontSize: 9,
  },
  spacer: {
    marginTop: 10,
  },
  // Tabel
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderColor: "#000",
  },
  tableRowLast: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  cellBase: {
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderColor: "#000",
    paddingHorizontal: 4,
    paddingVertical: 3,
    fontSize: 8,
  },
  cellLastRow: {
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingHorizontal: 4,
    paddingVertical: 3,
    fontSize: 8,
  },
  cellHeader: {
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderColor: "#000",
    paddingHorizontal: 4,
    paddingVertical: 4,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },
  // Terbilang & Total
  terbilangRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000",
    marginTop: 0,
  },
  terbilangCell: {
    flex: 1,
    padding: 4,
    fontSize: 8,
    fontFamily: "Helvetica-BoldOblique",
  },
  totalLabel: {
    width: "15%",
    borderLeftWidth: 1,
    borderColor: "#000",
    padding: 4,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
  },
  totalValue: {
    width: "15%",
    borderLeftWidth: 1,
    borderColor: "#000",
    padding: 4,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
  },
  // TTD
  ttdSection: {
    flexDirection: "row",
    marginTop: 30,
    justifyContent: "space-around",
  },
  ttdBox: {
    alignItems: "center",
    width: "22%",
  },
  ttdLabel: {
    fontSize: 8,
    marginBottom: 40,
    textAlign: "center",
  },
  ttdLine: {
    borderTopWidth: 1,
    borderColor: "#000",
    width: "100%",
    marginBottom: 3,
  },
  ttdName: {
    fontSize: 8,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
  },
});

const fmt = (val) =>
  parseFloat(val || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

const BlankoPOReturn = (props) => {
  const data = { ...props.data, Data: props.data?.Data ?? [] };

  const ColHeader = ({ width, title, align = "left" }) => (
    <Text style={[styles.cellHeader, { width: width + "%", textAlign: align }]}>
      {title}
    </Text>
  );

  const Col = ({ width, title, align = "left", bold = false, isLast = false }) => (
    <Text
      style={[
        isLast ? styles.cellLastRow : styles.cellBase,
        { width: `${width}%`, textAlign: align },
        bold ? { fontFamily: "Helvetica-Bold" } : {},
      ]}
    >
      {String(title ?? "")}
    </Text>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>

        {/* Header: Logo + Judul */}
        <View style={styles.headerRow}>
          <Image style={styles.logo} src={LOGO_RKS} />
          <View style={styles.titleBlock}>
            <Text style={styles.titleMain}>BUKTI BANK KELUAR</Text>
            {/* <Text style={styles.titleSub}>{data.PembayaranAPID}</Text> */}
          </View>
        </View>
        <View style={styles.divider} />

        {/* Info Header 2 Kolom */}
        <View style={styles.infoSection}>
          <View style={styles.infoLeft}>
            <Text style={styles.infoLabel}>Nama Penerima</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValue}>{data.DibayarKepada}</Text>
          </View>
          <View style={styles.infoRight}>
            <Text style={styles.infoLabel}>No. Bukti</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValue}>{data.PembayaranAPID}</Text>
          </View>
        </View>
        <View style={styles.infoSection}>
          <View style={styles.infoLeft}>
            <Text style={styles.infoLabel}>Telepon</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValueNormal}>-</Text>
          </View>
          <View style={styles.infoRight}>
            <Text style={styles.infoLabel}>Tanggal</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValueNormal}>
              {new Date(data.Tanggal).toLocaleDateString("id-ID", {
                day: "2-digit", month: "long", year: "numeric",
              })}
            </Text>
          </View>
        </View>
        <View style={styles.infoSection}>
          <View style={styles.infoLeft}>
            <Text style={styles.infoLabel}>Alamat</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValueNormal}>{data.Alamat || "-"}</Text>
          </View>
          <View style={styles.infoRight}>
            <Text style={styles.infoLabel}>{data.JenisPembayaran || "Jenis Bayar"}</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValueNormal}>{data.DetailAccount2}</Text>
          </View>
        </View>
        <View style={styles.infoSection}>
          <View style={styles.infoLeft}>
            <Text style={styles.infoLabel}>Keterangan</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValueNormal}>{data.Keterangan}</Text>
          </View>
          <View style={styles.infoRight}>
            <Text style={styles.infoLabel}>No. Cek / Giro</Text>
            <Text style={styles.infoColon}>:</Text>
            <Text style={styles.infoValueNormal}>{data.NoCek || "-"}</Text>
          </View>
        </View>

        <View style={styles.spacer} />

        {/* Tabel Header */}
        <View style={styles.tableHeader}>
          <ColHeader width={5}  title="No" align="center" />
          <ColHeader width={35} title="Nama Perkiraan" />
          <ColHeader width={8}  title="D/K" align="center" />
          <ColHeader width={22} title="No. Perkiraan" align="center" />
          <ColHeader width={15} title="Jumlah (USD $)" align="right" />
          <ColHeader width={15} title="Jumlah (Rp)" align="right" />
        </View>

        {/* Baris 1 – akun kredit */}
        <View style={styles.tableRow}>
          <Col width={5}  title="1" align="center" />
          <Col width={35} title={data.DetailAccount} />
          <Col width={8}  title="D" align="center" />
          <Col width={22} title={data.DetailAccountID} align="center" />
          <Col width={15} title="0" align="right" />
          <Col width={15} title={fmt(data.NilaiBayar)} align="right" />
        </View>

        {/* Baris 2 – akun debet utama */}
        <View style={styles.tableRow}>
          <Col width={5}  title="2" align="center" />
          <Col width={35} title={data.DetailAccount2} />
          <Col width={8}  title="K" align="center" />
          <Col width={22} title={data.DetailPembayaran} align="center" />
          <Col width={15} title="0" align="right" />
          <Col width={15} title={fmt(data.Nominal)} align="right" />
        </View>

        {/* Baris downpayment sebagai debet jika ada */}
        {data.DownpaymentCashJournalID ? (
          <View style={styles.tableRow}>
            <Col width={5}  title="3" align="center" />
            <Col width={35} title={data.DownpaymentAccountName || data.DownpaymentAccountID} />
            <Col width={8}  title="D" align="center" />
            <Col width={22} title={data.DownpaymentAccountID} align="center" />
            <Col width={15} title="0" align="right" />
            <Col width={15} title={fmt(data.DownpaymentAmount)} align="right" />
          </View>
        ) : null}

        {/* Baris detail tambahan */}
        {data.Data.map((d, i) => (
          <View key={i} style={styles.tableRow}>
            <Col width={5}  title={data.DownpaymentCashJournalID ? i + 4 : i + 3} align="center" />
            <Col width={35} title={d.DetailAccount} />
            <Col width={8}  title="D" align="center" />
            <Col width={22} title={d.Account} align="center" />
            <Col width={15} title="0" align="right" />
            <Col width={15} title={fmt(d.Nominal)} align="right" />
          </View>
        ))}

        {/* Baris terbilang + total */}
        <View style={styles.terbilangRow}>
          <Text style={styles.terbilangCell}>
            Terbilang : {angkaTerbilang(data.NilaiBayar)} rupiah
          </Text>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalValue}>{fmt(data.NilaiBayar)}</Text>
        </View>

        {/* TTD */}
        <View style={styles.ttdSection}>
          {[
            { label: "Diterima oleh", name: data.DibayarKepada },
            { label: "Dibuat oleh", name: ".................." },
            { label: "Diperiksa oleh", name: "Nasrul" },
            { label: "Disetujui oleh", name: "Aldi Harris" },
          ].map((ttd, i) => (
            <View key={i} style={styles.ttdBox}>
              <Text style={styles.ttdLabel}>{ttd.label}</Text>
              <View style={styles.ttdLine} />
              <Text style={styles.ttdName}>{ttd.name}</Text>
            </View>
          ))}
        </View>

      </Page>
    </Document>
  );
};

const Vocherpembayaran = () => {
  const params = useParams();
  const mutationGetBlanko = useMutation((formData) =>
    fetchGet("Accounting/Mon/PembayaranAP/Cetak/" + formData)
  );
  // User SO hanya boleh membuka voucher unitnya sendiri (halaman ini bisa dicapai
  // lewat URL langsung). BisnisUnitID dokumen berasal dari PurchaseRequisition —
  // ditambahkan ke response CetakVocherPembayaran khusus untuk pemeriksaan ini.
  const { bisnisUnitID: userBisnisUnit, resolved: buResolved } =
    useUserBisnisUnit();

  useEffect(() => {
    if (params.id) {
      mutationGetBlanko.mutate(params.id);
    }
  }, []);

  const rowBisnisUnit = mutationGetBlanko.data?.data?.BisnisUnitID || "";
  const isForeignUnit = rowBisnisUnit !== "" && rowBisnisUnit !== SO_UNIT;
  const accessDenied = userBisnisUnit === SO_UNIT && isForeignUnit;
  // Sesi lama: BU user baru diketahui setelah fetch — dokumen unit lain ditahan
  // dulu, bukan ditolak (user HO tetap boleh membukanya).
  const holdUntilResolved = !buResolved && isForeignUnit;

  if (accessDenied || holdUntilResolved) {
    return (
      <div className="md:ml-64" style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="max-w-md px-6 text-center">
          {accessDenied ? (
            <>
              <p className="text-lg font-bold text-gray-800 mb-2">Akses Ditolak</p>
              <p className="text-sm text-gray-600 mb-4">
                Voucher {params.id || "ini"} milik Bisnis Unit lain. Akun Anda
                hanya dapat membuka dokumen SO.
              </p>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Kembali
              </button>
            </>
          ) : (
            <p className="text-gray-600">Memeriksa hak akses...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="md:ml-64" style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {mutationGetBlanko.isLoading && (
        <div style={{ textAlign: "center", padding: "40px", fontSize: 16, color: "#555" }}>
          Memuat voucher...
        </div>
      )}
      {mutationGetBlanko.data && (
        <PDFViewer style={{ flex: 1, width: "100%", border: "none" }}>
          <BlankoPOReturn data={mutationGetBlanko.data.data} />
        </PDFViewer>
      )}
    </div>
  );
};

export default Vocherpembayaran;
